import { ArticleWithRelations } from '@/models/request';

// Returns true if there were no changes done between two snapshots
// Make sure this is kept in sync with the one in api/src/models/articles/article-snapshots.helper.ts
export const areArticleSnashotsEqual = (article: ArticleWithRelations): boolean => {
  const snapshot1 = article.snapshot;
  const snapshot2 = article.previousSnapshot;

  if (!snapshot2) {
    return false;
  }

  const areDefectsEqual =
    arraysHaveSameMembers(snapshot1.articleDefects, snapshot2.articleDefects, 'stableId') &&
    snapshot1.articleDefects.every((defect1) => {
      const defect2 = snapshot2.articleDefects.find(
        ({ stableId }) => stableId === defect1.stableId
      )!;

      return (
        defect1.customDescription === defect2.customDescription &&
        defect1.toBeRepaired === defect2.toBeRepaired &&
        arraysHaveSameMembers(defect1.media, defect2.media, 'id') &&
        defect1.context === defect2.context &&
        defect1.brandResponsibility === defect2.brandResponsibility &&
        defect1.exceptionalBrandResponsibilityCoverageRequested ===
          defect2.exceptionalBrandResponsibilityCoverageRequested &&
        defect1.exceptionalBrandResponsibilityCoverageReason ===
          defect2.exceptionalBrandResponsibilityCoverageReason &&
        defect1.exceptionalBrandResponsibilityCoverageOriginalRequestId ===
          defect2.exceptionalBrandResponsibilityCoverageOriginalRequestId
      );
    });

  const areActionsEqual =
    arraysHaveSameMembers(snapshot1.articleActions, snapshot2.articleActions, 'stableId') &&
    snapshot1.articleActions.every((action1) => {
      const action2 = snapshot2.articleActions.find(
        ({ stableId }) => stableId === action1.stableId
      )!;

      const areSparePartsEqual =
        arraysHaveSameMembers(action1.spareParts, action2.spareParts, 'stableId') &&
        action1.spareParts.every((sparePart1) => {
          const sparePart2 = action2.spareParts.find(
            ({ stableId }) => stableId === sparePart1.stableId
          )!;

          return (
            sparePart1.referenceNumber === sparePart2.referenceNumber &&
            sparePart1.quantity === sparePart2.quantity &&
            sparePart1.catalogOrganizationPrice?.amount ===
              sparePart2.catalogOrganizationPrice?.amount &&
            sparePart1.customOrganizationPrice?.amount ===
              sparePart2.customOrganizationPrice?.amount &&
            sparePart1.catalogWorkshopPrice?.amount === sparePart2.catalogWorkshopPrice?.amount &&
            sparePart1.customWorkshopPrice?.amount === sparePart2.customWorkshopPrice?.amount
          );
        });

      return (
        action1.customDescription === action2.customDescription &&
        arraysHaveSameMembers(
          action1.articleActionToArticleDefects.map(
            (articleActionToArticleDefects) => articleActionToArticleDefects.articleDefect
          ),
          action2.articleActionToArticleDefects.map(
            (articleActionToArticleDefects) => articleActionToArticleDefects.articleDefect
          ),
          'stableId'
        ) &&
        action1.catalogOrganizationPrice?.amount === action2.catalogOrganizationPrice?.amount &&
        action1.customOrganizationPrice?.amount === action2.customOrganizationPrice?.amount &&
        action1.catalogWorkshopPrice?.amount === action2.catalogWorkshopPrice?.amount &&
        action1.customWorkshopPrice?.amount === action2.customWorkshopPrice?.amount &&
        arraysHaveSameMembers(action1.media, action2.media, 'id') &&
        action1.brandResponsibility === action2.brandResponsibility &&
        action1.context === action2.context &&
        areSparePartsEqual
      );
    });

  let areExchangesEqual = true;

  if (snapshot1.articleExchange && snapshot2.articleExchange) {
    areExchangesEqual =
      snapshot1.articleExchange.replacementProductId ===
        snapshot2.articleExchange.replacementProductId &&
      snapshot1.articleExchange.replacementColor === snapshot2.articleExchange.replacementColor &&
      snapshot1.articleExchange.replacementSize === snapshot2.articleExchange.replacementSize &&
      snapshot1.articleExchange.type === snapshot2.articleExchange.type &&
      snapshot1.articleExchange.customOrganizationPrice?.amount ===
        snapshot2.articleExchange.customOrganizationPrice?.amount &&
      snapshot1.articleExchange.customWorkshopPrice?.amount ===
        snapshot2.articleExchange.customWorkshopPrice?.amount &&
      snapshot1.articleExchange.stockLocationId === snapshot2.articleExchange.stockLocationId;
  }

  const areServiceChoicesEqual = snapshot1.serviceChoice === snapshot2.serviceChoice;

  return areDefectsEqual && areActionsEqual && areExchangesEqual && areServiceChoicesEqual;
};

const arraysHaveSameMembers = <T>(array1: T[], array2: T[], key: keyof T): boolean => {
  return (
    array1.length === array2.length &&
    array1.every((itemFrom1) => !!array2.find((itemFrom2) => itemFrom2[key] === itemFrom1[key]))
  );
};
